import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import './Navbar.css';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { getCartCount } = useCart();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const desktopUserMenuRef = useRef(null);
  const mobileUserMenuRef = useRef(null);
  const cartCount = getCartCount();

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    await logout();
    navigate('/');
  };

  const getCartCountClass = () => {
    if (cartCount > 10) return 'critical';
    if (cartCount > 5) return 'warning';
    return '';
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideDesktop = !desktopUserMenuRef.current || !desktopUserMenuRef.current.contains(event.target);
      const clickedOutsideMobile = !mobileUserMenuRef.current || !mobileUserMenuRef.current.contains(event.target);
      if (clickedOutsideDesktop && clickedOutsideMobile) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const renderNavLinks = (isMobile = false) => (
    <>
      <Link
        to="/events"
        className={`nav-link ${location.pathname === '/events' ? 'active' : ''}`}
        onClick={handleLinkClick}
      >
        Events
      </Link>

      {user && (
        <Link
          to="/profile"
          className={`nav-link ${location.pathname === '/profile' ? 'active' : ''}`}
          onClick={handleLinkClick}
        >
          My Tickets
        </Link>
      )}

      {user?.admin && (
        <Link
          to="/dashboard"
          className={`nav-link ${location.pathname.startsWith('/dashboard') ? 'active' : ''}`}
          onClick={handleLinkClick}
        >
          Admin
        </Link>
      )}

      {user?.planner && (
        <Link
          to="/planner"
          className={`nav-link ${location.pathname.startsWith('/planner') ? 'active' : ''}`}
          onClick={handleLinkClick}
        >
          Planner
        </Link>
      )}

      {user?.staff && (
        <Link
          to="/staff"
          className={`nav-link ${location.pathname.startsWith('/staff') ? 'active' : ''}`}
          onClick={handleLinkClick}
        >
          Staff
        </Link>
      )}

      {(() => {
        const isSaleAgent = user?.isSaleAgent || user?.saleAgent || user?.roles?.includes('SALE_AGENT');
        return isSaleAgent;
      })() && (
          <Link
            to="/pos"
            className={`nav-link ${location.pathname.startsWith('/pos') ? 'active' : ''}`}
            onClick={handleLinkClick}
          >
            POS
          </Link>
        )}
    </>
  );

  const mobileToggleButton = (
    <button
      className="mobile-toggle"
      onClick={toggleMobileMenu}
      aria-label="Toggle navigation"
    >
      {isMobileMenuOpen ? '✕' : '☰'}
    </button>
  );

  const navClass = `nav-shell ${isMobileMenuOpen ? 'nav-mobile-open' : ''} ${user ? '' : 'nav-logged-out'}`;

  return (
    <nav className={navClass}>
      <div className="nav-glass">
        <div className="nav-left">
          <Link to="/" className="brand-mark" onClick={handleLinkClick}>
            <span className="brand-text">TikPlat</span>
          </Link>

        </div>

        {/* Desktop Menu */}
        <div className="nav-main desktop-only">
          <div className="nav-primary">
            {renderNavLinks()}
          </div>

          <div className="nav-actions">
            {user ? (
              <>
                <Link to="/cart" className="cart-button" onClick={handleLinkClick}>
                  <span className="cart-icon">🛒</span>
                  Cart
                  {cartCount > 0 && (
                    <span className={`cart-count ${getCartCountClass()}`}>
                      {cartCount}
                    </span>
                  )}
                </Link>

                <div className="user-menu" ref={desktopUserMenuRef}>
                  <button
                    className="user-button"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  >
                    <span className="avatar">
                      {(user.firstName || user.email || 'A').charAt(0).toUpperCase()}
                    </span>
                    <span className="user-meta">
                      <span className="user-label">Signed in</span>
                      <span className="user-name">{user.firstName || user.email}</span>
                    </span>
                  </button>
                  {isUserMenuOpen && (
                    <div className="user-menu-dropdown">
                      <Link
                        to="/profile"
                        className="user-menu-link"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          handleLinkClick();
                        }}
                      >
                        Profile
                      </Link>
                      <button className="user-menu-button" onClick={handleLogout}>
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`nav-link subtle ${location.pathname === '/login' ? 'active' : ''}`}
                  onClick={handleLinkClick}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className={`nav-link nav-cta ${location.pathname === '/register' ? 'active' : ''}`}
                  onClick={handleLinkClick}
                >
                  Register
                </Link>
              </>
            )}
          </div>
        </div>
        {mobileToggleButton}
      </div>

      {/* Mobile Menu - Outside nav-glass */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'show' : ''}`}>
        <div className="nav-primary">
          {renderNavLinks(true)}
        </div>

        {user && (
          <div className="mobile-profile-section">
            <div className="mobile-user-info">
              <div className="mobile-avatar">
                {(user.firstName || user.email || 'A').charAt(0).toUpperCase()}
              </div>
              <div className="mobile-user-details">
                <div className="mobile-user-name">
                  {user.firstName ? user.firstName : 'User'}
                </div>
                <div className="mobile-user-email">{user.email}</div>
              </div>
            </div>
            <div className="mobile-profile-actions">
              <Link
                to="/profile"
                className="mobile-action-btn"
                onClick={() => {
                  handleLinkClick();
                }}
                title="Profile"
              >
                👤
              </Link>
              <button
                className="mobile-action-btn logout"
                onClick={handleLogout}
                title="Logout"
              >
                ⏻
              </button>
            </div>
          </div>
        )}

        <div className="nav-actions">
          {user ? (
            <Link to="/cart" className="cart-button" onClick={handleLinkClick}>
              <span className="cart-icon">🛒</span>
              Cart
              {cartCount > 0 && (
                <span className={`cart-count ${getCartCountClass()}`}>
                  {cartCount}
                </span>
              )}
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className={`nav-link subtle ${location.pathname === '/login' ? 'active' : ''}`}
                onClick={handleLinkClick}
              >
                Login
              </Link>
              <Link
                to="/register"
                className={`nav-link nav-cta ${location.pathname === '/register' ? 'active' : ''}`}
                onClick={handleLinkClick}
              >
                Register
              </Link>
            </>
          )}
        </div>
      </div>

      {isMobileMenuOpen && <div className="nav-overlay" onClick={toggleMobileMenu} />}
    </nav>
  );
};

export default Navbar;
